import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState, Children, useRef } from 'react';
import { HelixPalette, HelixStyles } from '../../helix';
import { Button } from '../Button';
import { Divider } from '../Divider';
import { Dropdown } from '../Dropdown';
import { IChevronLeft } from '../Iconography';
import { MenuContext } from './MenuContext';
import { MenuItem } from './MenuItem';
import { MenuItemGroup } from './MenuItemGroup';
import { findFirstMenuItemId, buildMenuItemsMap } from './Menu.helpers';
import { HELIX_REACT_CORE_TRANSLATION_NAMESPACE } from '../../locales';
/**
 * Reusable menu component with support for hierarchical sub-menus using compound components
 */
export const Menu = ({ dataId, style, children, open, anchorEl, onClose, width = 320, placement = 'bottom', offset = [100, 8] }) => {
    // Navigation state for compound component API
    const [navigationState, setNavigationState] = useState({
        currentContent: children,
        contentStack: [],
        currentTitle: undefined,
        titleStack: [],
    });
    const { t } = useTranslation();
    // State to track which menu item should be focused
    const [firstMenuItemId, setFirstMenuItemId] = useState(null);
    const [focusedItemId, setFocusedItemId] = useState(null);
    const [focusedElementType, setFocusedElementType] = useState('menu-item');
    // Ref for the menu container to handle focus
    const menuContainerRef = useRef(null);
    const backButtonRef = useRef(null);
    // Reset navigation state when menu opens and set focus to first item
    useEffect(() => {
        if (open) {
            setNavigationState({
                currentContent: children,
                contentStack: [],
                currentTitle: undefined,
                titleStack: [],
            });
            // Find and set the first menu item to focus
            const firstItemId = findFirstMenuItemId(children, MenuItemGroup, MenuItem);
            setFirstMenuItemId(firstItemId);
            setFocusedItemId(firstItemId);
            setFocusedElementType('menu-item');
        }
    }, [open, children]);
    // Update first menu item when navigation state changes
    useEffect(() => {
        if (open) {
            const firstItemId = findFirstMenuItemId(navigationState.currentContent, MenuItemGroup, MenuItem);
            setFirstMenuItemId(firstItemId);
            setFocusedItemId(firstItemId);
            setFocusedElementType('menu-item');
        }
    }, [navigationState.currentContent, open]);
    /**
     * Handle keyboard navigation within the menu
     */
    const handleMenuKeyDown = (event) => {
        if (!open)
            return;
        const groups = buildMenuItemsMap(navigationState.currentContent, MenuItemGroup, MenuItem);
        switch (event.key) {
            case 'ArrowDown':
            case 'ArrowUp':
                // Arrow keys only work within menu item groups
                if (focusedElementType === 'menu-item' && focusedItemId) {
                    event.preventDefault();
                    let currentGroupIndex = -1;
                    let currentItemIndex = -1;
                    // Find current position within groups
                    for (let groupIdx = 0; groupIdx < groups.length; groupIdx++) {
                        const itemIdx = groups[groupIdx].items.indexOf(focusedItemId);
                        if (itemIdx !== -1) {
                            currentGroupIndex = groupIdx;
                            currentItemIndex = itemIdx;
                            break;
                        }
                    }
                    if (currentGroupIndex !== -1) {
                        const currentGroup = groups[currentGroupIndex];
                        let newFocusedItemId;
                        if (event.key === 'ArrowDown') {
                            // Move to next item in current group
                            if (currentItemIndex < currentGroup.items.length - 1) {
                                newFocusedItemId = currentGroup.items[currentItemIndex + 1];
                            }
                            else {
                                // Wrap to first item in current group
                                newFocusedItemId = currentGroup.items[0];
                            }
                        }
                        else {
                            // ArrowUp - Move to previous item in current group
                            if (currentItemIndex > 0) {
                                newFocusedItemId = currentGroup.items[currentItemIndex - 1];
                            }
                            else {
                                // Wrap to last item in current group
                                newFocusedItemId = currentGroup.items[currentGroup.items.length - 1];
                            }
                        }
                        setFocusedItemId(newFocusedItemId);
                        setFocusedElementType('menu-item');
                    }
                }
                break;
            case 'Tab':
                event.preventDefault();
                if (focusedElementType === 'back-button') {
                    // From back button, move to first item of first group
                    const firstItemId = findFirstMenuItemId(navigationState.currentContent, MenuItemGroup, MenuItem);
                    if (firstItemId) {
                        setFocusedElementType('menu-item');
                        setFocusedItemId(firstItemId);
                    }
                }
                else if (focusedElementType === 'menu-item' && focusedItemId) {
                    // Find which group the currently focused item belongs to
                    let currentGroupIndex = -1;
                    for (let groupIdx = 0; groupIdx < groups.length; groupIdx++) {
                        if (groups[groupIdx].items.includes(focusedItemId)) {
                            currentGroupIndex = groupIdx;
                            break;
                        }
                    }
                    if (currentGroupIndex !== -1) {
                        if (event.shiftKey) {
                            // Shift+Tab - move to previous group or back button
                            if (currentGroupIndex > 0) {
                                // Move to first item of previous group
                                const prevGroupFirstItem = groups[currentGroupIndex - 1].items[0];
                                setFocusedItemId(prevGroupFirstItem);
                                setFocusedElementType('menu-item');
                            }
                            else {
                                // Move to back button if it exists, otherwise wrap to last group
                                const hasBackButton = navigationState.contentStack.length > 0;
                                if (hasBackButton) {
                                    setFocusedElementType('back-button');
                                    setFocusedItemId(null);
                                    setTimeout(() => {
                                        if (backButtonRef.current) {
                                            backButtonRef.current.focus({ preventScroll: true });
                                        }
                                    }, 0);
                                }
                                else {
                                    // Wrap to last group
                                    const lastGroupFirstItem = groups[groups.length - 1].items[0];
                                    setFocusedItemId(lastGroupFirstItem);
                                    setFocusedElementType('menu-item');
                                }
                            }
                        }
                        else {
                            // Tab - move to next group or wrap around
                            if (currentGroupIndex < groups.length - 1) {
                                // Move to first item of next group
                                const nextGroupFirstItem = groups[currentGroupIndex + 1].items[0];
                                setFocusedItemId(nextGroupFirstItem);
                                setFocusedElementType('menu-item');
                            }
                            else {
                                // Wrap to beginning (back button if exists, otherwise first group)
                                const hasBackButton = navigationState.contentStack.length > 0;
                                if (hasBackButton) {
                                    setFocusedElementType('back-button');
                                    setFocusedItemId(null);
                                    // Use setTimeout to defer focus until after React state updates
                                    setTimeout(() => {
                                        if (backButtonRef.current) {
                                            backButtonRef.current.focus({ preventScroll: true });
                                        }
                                    }, 0);
                                }
                                else {
                                    // Wrap to first group
                                    const firstGroupFirstItem = groups[0].items[0];
                                    setFocusedItemId(firstGroupFirstItem);
                                    setFocusedElementType('menu-item');
                                }
                            }
                        }
                    }
                }
                break;
        }
    };
    /**
     * Navigate to a sub-menu
     */
    const navigateToSubMenu = (title, content) => {
        setNavigationState((prev) => ({
            currentContent: content,
            contentStack: [...prev.contentStack, prev.currentContent],
            currentTitle: title,
            titleStack: [...prev.titleStack, prev.currentTitle || ''],
        }));
    };
    /**
     * Navigate back to parent menu
     */
    const navigateBack = () => {
        setNavigationState((prev) => {
            if (prev.contentStack.length === 0)
                return prev;
            const previousContent = prev.contentStack[prev.contentStack.length - 1];
            const previousTitle = prev.titleStack[prev.titleStack.length - 1];
            return {
                currentContent: previousContent,
                contentStack: prev.contentStack.slice(0, -1),
                currentTitle: previousTitle || undefined,
                titleStack: prev.titleStack.slice(0, -1),
            };
        });
    };
    /**
     * Render back button for sub-menus
     */
    const renderBackButton = () => {
        const hasNavigationStack = navigationState.contentStack.length > 0;
        const currentTitle = navigationState.currentTitle;
        if (!hasNavigationStack)
            return null;
        const handleBackButtonFocus = () => {
            setFocusedElementType('back-button');
            setFocusedItemId(null);
        };
        return (_jsxs(_Fragment, { children: [_jsxs(Box, Object.assign({ sx: {
                        display: 'flex',
                        alignItems: 'center',
                        paddingBottom: '4px',
                    } }, { children: [_jsx(Button, { dataId: `${dataId}-back-to-previous-menu`, leftIcon: _jsx(IChevronLeft, {}), onClick: navigateBack, onFocus: handleBackButtonFocus, title: t('menu.previousMenu', { ns: HELIX_REACT_CORE_TRANSLATION_NAMESPACE }), type: "universal", variant: "grey", size: "small", ref: backButtonRef }), currentTitle && (_jsx(Typography, Object.assign({ "data-id": `${dataId}-submenu-title`, style: Object.assign(Object.assign({}, HelixStyles.style_03), { color: HelixPalette.neutral90, marginLeft: '16px' }) }, { children: currentTitle })))] })), _jsx(Divider, { style: { margin: '0px', marginBottom: '8px' } })] }));
    };
    /**
     * Render all content with automatic dividers between MenuItemGroup components
     */
    const renderContent = () => {
        const content = navigationState.currentContent;
        if (!content)
            return null;
        // Process all children, adding dividers only between consecutive MenuItemGroup components
        const childrenArray = Children.toArray(content);
        const processedChildren = [];
        childrenArray.forEach((child, index) => {
            processedChildren.push(child);
            // Add divider between consecutive MenuItemGroup components
            const currentIsGroup = React.isValidElement(child) && child.type === MenuItemGroup;
            const nextChild = childrenArray[index + 1];
            const nextIsGroup = React.isValidElement(nextChild) && nextChild.type === MenuItemGroup;
            if (currentIsGroup && nextIsGroup) {
                processedChildren.push(_jsx(Divider, { style: {
                        margin: '8px 0',
                    } }, `auto-divider-${index}`));
            }
        });
        return processedChildren;
    };
    const contextValue = {
        dataId,
        onClose,
        navigateToSubMenu,
        navigationLevel: navigationState.contentStack.length,
        firstMenuItemId,
        focusedItemId,
        setFocusedItemId,
        focusedElementType,
    };
    return (_jsx(MenuContext.Provider, Object.assign({ value: contextValue }, { children: _jsx(Dropdown, Object.assign({ id: dataId, dataId: `${dataId}-menu`, enableClickAway: true, open: open, placement: placement, height: 800, onClose: onClose, anchorEl: anchorEl, enableAutoFocus: false, modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: offset,
                    },
                },
                {
                    name: 'preventOverflow',
                    options: {
                        boundary: 'viewport',
                        padding: 8,
                    },
                },
            ], style: Object.assign({ width: width, maxWidth: width }, style) }, { children: _jsxs(Box, Object.assign({ sx: {
                    display: 'flex',
                    padding: '8px',
                    flex: 1,
                    flexFlow: 'column nowrap',
                }, onKeyDown: handleMenuKeyDown, tabIndex: 0, role: "menu", "aria-activedescendant": focusedElementType === 'menu-item' ? focusedItemId || undefined : undefined, ref: menuContainerRef }, { children: [renderBackButton(), renderContent()] })) })) })));
};
// Attach compound components to Menu
Menu.Item = MenuItem;
Menu.ItemGroup = MenuItemGroup;
